import type { MapBasemap } from "./basemap.ts";
import { detectPlatform, loadKeymap, matchKey, type KeyState } from "./input/keymap.ts";
import { focusOwnsKey } from "./input/typing.ts";

const instances = new Set<MapBasemap>();

export function trackBasemapKeyboard(basemap: MapBasemap): void {
  instances.add(basemap);
}

export function untrackBasemapKeyboard(basemap: MapBasemap): void {
  instances.delete(basemap);
}

function keyState(e: KeyboardEvent): KeyState {
  return { code: e.code, alt: e.altKey, shift: e.shiftKey, ctrl: e.ctrlKey, meta: e.metaKey };
}

function isTyping(target: EventTarget | null, code: string): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const type = target instanceof HTMLInputElement ? target.type : undefined;
  return focusOwnsKey({ tagName: target.tagName, type, isContentEditable: target.isContentEditable }, code);
}

function prefStore(): { getItem(k: string): string | null; setItem(k: string, v: string): void; removeItem(k: string): void } | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

/**
 * Plain V (or a remapped toggleBasemap chord) toggles satellite on every visible map
 * that exposes the basemap control. Returns true when the key was handled.
 */
export function basemapToggleFromKey(
  e: KeyboardEvent,
  opts: { settingsOpen?: boolean; keymapCapture?: boolean },
): boolean {
  if (opts.keymapCapture || opts.settingsOpen) return false;
  if (e.repeat) return false;
  const store = prefStore();
  if (!store) return false;
  const platform = detectPlatform();
  const { keymap } = loadKeymap(store);
  if (matchKey(keymap, keyState(e), platform) !== "toggleBasemap") return false;
  if (isTyping(e.target, e.code)) return false;
  let toggled = false;
  for (const basemap of instances) {
    if (!basemap.canToggleFromKeyboard()) continue;
    void basemap.toggleFromKeyboard();
    toggled = true;
  }
  if (toggled) e.preventDefault();
  return toggled;
}
